import React from 'react'
import { Close } from '@mui/icons-material'
import { Box, Dialog, DialogContent, IconButton, Typography } from '@mui/material'
import { useStyles } from './style'

/**
 * Shared chrome for the notification modals (see NOTIFICATION_ACTION in
 * ../constants.js for which notification opens which). Same Dialog anatomy as
 * the accelerate achievement modals — close icon, optional hero, eyebrow,
 * title — kept self-contained here so a shared component never imports from a
 * page.
 *
 * `layout='side'` puts the hero in a left column (near-square brand assets fit
 * it without cropping); the default puts it as a top banner, where
 * `hero.position` can anchor the crop on the subject.
 */
export const BaseNotificationModal = ({ open, onClose, title, eyebrow, hero, layout = 'top', children, dataTest }) => {
  const classes = useStyles()
  const side = layout === 'side' && Boolean(hero)

  const header = (
    <>
      {eyebrow && (
        <Typography className={classes.eyebrow}>{eyebrow}</Typography>
      )}
      <Typography
        id='notification-modal-title'
        component='h2'
        variant='h5'
        className={`${classes.title} ${hero && !side ? '' : classes.titleClearsClose}`}
      >
        {title}
      </Typography>
    </>
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={side ? 'md' : 'sm'}
      fullWidth
      aria-labelledby='notification-modal-title'
      data-test={dataTest}
      PaperProps={{ className: classes.paper }}
      slotProps={{ backdrop: { className: classes.backdrop } }}
    >
      {/* Paper background behind the icon so it stays legible over a hero image */}
      <IconButton
        onClick={onClose}
        aria-label='Close'
        className={classes.closeButton}
        data-test={`${dataTest}-close`}
      >
        <Close aria-hidden='true' />
      </IconButton>
      {side
        ? (
          <Box className={classes.sideLayout}>
            <Box className={classes.sideImageWrap}>
              <img src={hero.src} alt={hero.alt || ''} className={classes.sideImage} />
            </Box>
            <DialogContent className={classes.content}>
              {header}
              {children}
            </DialogContent>
          </Box>
          )
        : (
          <>
            {hero && (
              <img
                src={hero.src}
                alt={hero.alt || ''}
                className={classes.bannerImage}
                style={hero.position ? { objectPosition: hero.position } : undefined}
              />
            )}
            <DialogContent className={classes.content}>
              {header}
              {children}
            </DialogContent>
          </>
          )}
    </Dialog>
  )
}

export default BaseNotificationModal
